import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type { MoveDirection, Person, PersonGroup, PersonPayload } from "@/types";

/**
 * React Query hooks for the admin People feature (KC-459). Copies the Sources
 * template: a key factory, one list query per group filter, and mutations that
 * invalidate the whole slice on success.
 */

export const personKeys = {
  all: ["admin", "people"] as const,
  list: (group?: PersonGroup) => [...personKeys.all, "list", group ?? "all"] as const,
};

export function useAdminPeople(group?: PersonGroup) {
  return useQuery<Person[]>({
    queryKey: personKeys.list(group),
    queryFn: () => api.getAdminPeople(group),
  });
}

function useInvalidatePeople() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: personKeys.all });
}

export function useCreatePerson() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: (data: PersonPayload) => api.createPerson(data),
    onSuccess: invalidate,
  });
}

export function useUpdatePerson() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PersonPayload> }) =>
      api.updatePerson(id, data),
    onSuccess: invalidate,
  });
}

export function useDeletePerson() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: (id: string) => api.deletePerson(id),
    onSuccess: invalidate,
  });
}

export function useMovePerson() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: MoveDirection }) =>
      api.movePerson(id, direction),
    onSuccess: invalidate,
  });
}

export function useUploadPersonPhoto() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => api.uploadPersonPhoto(id, file),
    onSuccess: invalidate,
  });
}
